import React, { useState, useRef, useEffect, useCallback } from 'react';
import './medicine.css';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import { products } from './constants';
import CustomPagination from '../../../components/custompagination/custompagination';
import { useNavigate } from 'react-router-dom';
import { validateRequired } from '../../../utils/common';
import Box from '@mui/material/Box';

import httpClient from '../../../services/http';
import { APIUtils } from '../../../utils/apiutils';
import { addToCart, updateCartOrderInfo } from '../../../redux/actions/cartactions';
import { useDispatch, useSelector } from 'react-redux';
import { CUSTOM_EVENTS } from '../../../utils/constants';
import IconButton from '@mui/material/IconButton';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import product from '../../../assets/images/product_image.svg';

function Medicine() {
	const navigate = useNavigate();
	const [medicines, setMedicines] = useState([]);
	const [medicineCategoryList, setMedicineCategoryList] = useState([]);

	const dispatch = useDispatch();
	const cartTracker = useSelector(state => state.cart.cartTracker);

	const [page, setPage] = useState(1);
	const [limit] = useState(4);
	const [totalPages, setTotalPages] = useState(1);

	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});
	const formSubmitted = useRef(false);
	const intervalsRef = useRef({});
	const [fadeStates, setFadeStates] = useState({});
	const [slideOffsets, setSlideOffsets] = useState({});
	const [isTransitioning, setIsTransitioning] = useState({});
	const carouselRefs = useRef({});

	const goToDetailsPage = () => {
		navigate('/medicinedetails/1');
	};

	const prescriptionsMedicines = [
		{ value: '', label: '--  Prescription Medicine   --' },
		{ value: '1', label: 'Wellness' },
		{ value: '2', label: 'Beauty' },
		{ value: '3', label: 'Fitness' },
	];

	const requiredFields = ['prescriptionMedicine', 'searchQuery'];
	const setFormValue = (field, value) => {
		const tmp = { ...formData, [field]: value };
		setFormData(tmp);
		if (formSubmitted.current) {
			const errors = validateRequired(requiredFields, tmp);
			setErrors(errors);
		}
	};

	const handleSearch = async () => {
		formSubmitted.current = true;
		const errors = validateRequired(requiredFields, formData);
		if (errors) {
			setErrors(errors); return;
		}
		setPage(1); // reset to page 1 on search
		try {
			const res = await httpClient.get(APIUtils.getAPIEndpoint('medicine'), {
				params: {
					page: 1,
					limit,
					categoryId: formData.prescriptionMedicine || undefined,
					search: formData.searchQuery || undefined
				}
			}).then(res => res.data);
			if (res.status === 'success') {
				setMedicines(res.data);
				setTotalPages(res.meta.totalPages);
			} else {
				setMedicines([]);
				setTotalPages(1);
			}
		} catch (err) {
			console.error('Search failed:', err);
			setMedicines([]);
			setTotalPages(1);
		}
	};

	const fetchMedicines = useCallback( async () => {
		try {
			// const response = await httpClient.get('/api/admin/medicine');
			const res = await httpClient.get(APIUtils.getAPIEndpoint('medicine'), {
				params: { page, limit }
			}).then(res => res.data);
			if (res.status === 'success') {
				setMedicines(res.data);
				setTotalPages(res.meta.totalPages);
				const initialIndices = {};
				res.data.forEach((medicine, idx) => {
					initialIndices[medicine.id] = 0; // use medicine id instead of index for reliability
				});
				// const initialFade = {};
				const offsets = {};
				const transitions = {};
				// Start automatic carousel for each medicine
				res.data.forEach(med => {
					// initialIndices[med.id] = 0;
					offsets[med.id] = 0;
					transitions[med.id] = true;
					// initialFade[med.id] = true;
					const totalImages = med.images.length;
					if (totalImages > 1) {
						let index = 0;
						const intervalId = setInterval(() => {
							// setFadeStates(prev => ({ ...prev, [med.id]: false }));
							// setTimeout(() => {
								// index = (index + 1) % totalImages;
								index += 1;
								if (index === totalImages) {
									// Jump back to first after showing cloned one
									setTimeout(() => {
										setIsTransitioning(prev => ({ ...prev, [med.id]: false }));
										setSlideOffsets(prev => ({ ...prev, [med.id]: 0 }));
									}, 600); // match transition time
								}
								setIsTransitioning(prev => ({ ...prev, [med.id]: true }));
								setSlideOffsets(prev => ({ ...prev, [med.id]: index }));
								if (index >= totalImages) index = 0;
								// setSlideOffsets(prev => ({
								// 	...prev,
								// 	[med.id]: index,
								// }));
								// setFadeStates(prev => ({ ...prev, [med.id]: true }));
							// }, 500);
						}, 3000);
						carouselRefs.current[med.id] = intervalId;
					}
				});
				setSlideOffsets(offsets);
				setIsTransitioning(transitions);
				// setFadeStates(initialFade);
			} else {
				setMedicines([]);
				setTotalPages(1);
			}
		} catch (error) {
			console.error('Failed to fetch medicines:', error);
			setMedicines([]);
			setTotalPages(1);
		}
	},[page, limit]);

	// useEffect(() => {
	// 	fetchMedicines();
	// },[fetchMedicines]);
	useEffect(() => {
		fetchMedicines();
			return () => {
				Object.values(carouselRefs.current).forEach(clearInterval);
			};
	},[fetchMedicines]);
	const getMedicineCategoryList = useCallback(async () => {
		try {
			const res = await httpClient.get(APIUtils.getAPIEndpoint('medicinecategory'))
				.then(res => res.data)
				.then(res => res.data.map((item, index) => { return { ...item, index: index + 1 }; }));
			setMedicineCategoryList(res);
		} catch (error) {
			console.error(error);
			setMedicineCategoryList([]);
		}
	}, []);

	useEffect(() => {
		getMedicineCategoryList();
	}, [getMedicineCategoryList]);

	const handleReset = () => {
		setFormData({});
		setErrors({});
		formSubmitted.current = false;
		setPage(1);
		fetchMedicines();
	};

	const handleAddToCart = (medicine) => {
		const cartItem = {
			id: medicine.id,
			name: medicine.medicine_name,
			description: medicine.product_details,
			price: +medicine.price,
		};

		addToCart(cartItem);
		updateCartOrderInfo({orderType: 'Medicine'});

		document.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.TOGGLE_NOTIFY, {
			detail: {
			open: true,
			message: `${medicine.medicine_name} - Added to cart successfully.`,
			},
		}));
	};

	return (
		<Container
			sx={{
				padding: '40px',
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<Grid
				container
				spacing={2}
				sx={{
					display: 'flex',
					justifyContent: 'center',
				}}
			>
				<Grid item xs={12} sm={3}>
					<FormControl fullWidth>
						<TextField
							label='--   Prescription Medicine   --'
							value={formData.prescriptionMedicine || ''}
							fullWidth
							size='small'
							select
							onChange={e => setFormValue('prescriptionMedicine', e.target.value)}
							{...(errors.prescriptionMedicine ? { error: true, helperText: 'This field is required.' } : {})}
						>
							{medicineCategoryList.map(sm => <MenuItem key={`sm-menu-${sm.id}`} value={sm.id} name={sm.name}>{sm.name}</MenuItem>)}
						</TextField>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormControl fullWidth>
						<TextField
							label='Search Query'
							variant='outlined'
							size='small'
							fullWidth
							value={formData.searchQuery || ''}
							onChange={e => setFormValue('searchQuery', e.target.value)}
							{...(errors.searchQuery ? { error: true, helperText: 'This field is required.' } : {})}
						/>
					</FormControl>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormControl fullWidth>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Button variant='contained' fullWidth className='btn-square' onClick={handleSearch}>
								Search
							</Button>
							<IconButton
								color="primary"
								disabled={!formData.prescriptionMedicine && !formData.searchQuery}
								onClick={handleReset}
							>
								<RestartAltIcon />
							</IconButton>
						</Box>
					</FormControl>
				</Grid>
			</Grid>
			<Grid
				container
				spacing={2}
				sx={{
					marginTop: '20px',
					display: 'flex',
					rowGap: '20px',
					columnGap: '30px',
					justifyContent: 'center',
				}}
				columns={{ xs: 1, sm: 8, md: 12 }}
			>
				{medicines.map(({ images, medicine_name, price, id }, index) => (
					<Grid key={'medicine-' + index}>
						<Card
							sx={{
								width: 260,
								height: 300,
								padding: '14px',
								boxSizing: 'border-box',
							}}
							// onClick={goToDetailsPage}
						>
							{images.length === 0 ? (
								<CardMedia
									component='img'
									height='120'
									image={product} // fallback/default image
									sx={{ objectFit: 'contain' }}
								/>
							) : (
								<div style={{
									width: '100%',
									height: '120px',
									overflow: 'hidden',
									position: 'relative',
								}}>
									<div
										style={{
											display: 'flex',
											width: `${(images.length + 1) * 100}%`,
											transform: `translateX(-${(slideOffsets[id] || 0) * (100 / (images.length + 1))}%)`,
											transition: isTransitioning[id] ? 'transform 0.6s ease-in-out' : 'none',
										}}
									>
										{[...images, images[0]].map((img, i) => (
											<CardMedia
												key={i}
												component='img'
												image={img.imageUrl}
												alt={`medicine-${i}`}
												sx={{
													width: `${100 / (images.length + 1)}%`,
													height: '120px',
													objectFit: 'contain',
												}}
											//   onClick={goToDetailsPage}
											/>
										))}
									</div>
								</div>
							)}
							<CardContent
								style={{ padding: 4 }}
								sx={{ height: '40px', boxSizing: 'border-box' }}
							>
								<Typography gutterBottom className='doc-name'>
									{medicine_name}
								</Typography>
							</CardContent>
							<Typography gutterBottom className='doc-name'>
								Price:{price}
							</Typography>
							<CardActions
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								<Button
									size='small'
									variant='contained'
									className='btn-square'
									onClick={() => handleAddToCart(medicines[index])}
								>
									Add to Cart
								</Button>
							</CardActions>
						</Card>
					</Grid>
				))}
			</Grid>
			<Grid
				sx={{
					margin: '20px',
				}}
			>
				<CustomPagination
					count={totalPages}
					page={page}
					onChange={(event, value) => setPage(value)}
				/>
			</Grid>
		</Container >
	);
}
export default Medicine;
